import { createClient } from '@supabase/supabase-js';
import imageCompression from 'browser-image-compression';
import { CHART_OF_ACCOUNTS } from '@/constants/chartOfAccounts';
import { ensureSandboxInitialized } from '@/utils/devTestingSandbox';

const isProduction = typeof window !== 'undefined' && window.location.hostname.includes('sataladermatechessential.com');
const supabaseUrl = 'https://db.sataladermatechessential.com';
const supabaseKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJyb2xlIjoiYW5vbiIsImlzcyI6InN1cGFiYXNlIiwiaWF0IjoxNzgwMjIxMTk3LCJleHAiOjE5Mzc5MDExOTd9.o9VzBr1CisDKFlNoSDQaW2gTbiasqS2_Y-qgqdEbRpg';
export const supabase = createClient(supabaseUrl, supabaseKey);

const fetchAllRowsFromTable = async (entityName, filterObj = null) => {
  let allRows = [];
  let page = 0;
  const pageSize = 1000;
  let hasMore = true;

  while (hasMore) {
    let req = supabase
      .from(entityName)
      .select('*')
      .order('created_date', { ascending: false })
      .range(page * pageSize, (page + 1) * pageSize - 1);

    if (filterObj) {
      for (const key of Object.keys(filterObj)) {
        if (['id', 'created_date', 'updated_date'].includes(key)) {
          req = req.eq(key, filterObj[key]);
        } else {
          req = req.eq(`data->>${key}`, filterObj[key]);
        }
      }
    }

    const { data, error } = await req;
    if (error) {
      console.error(`[Supabase] Gagal membaca tabel ${entityName}:`, error.message);
      break;
    }

    if (data && data.length > 0) {
      allRows.push(...data);
      if (data.length < pageSize) {
        hasMore = false;
      } else {
        page++;
      }
    } else {
      hasMore = false;
    }
  }

  return allRows;
};

export const ENTITY_METADATA = {
  // Production
  ProductionOrder: { portal: 'production', page: 'po_list', label: 'Daftar PO Produksi' },
  StokItem: { portal: 'production', page: 'inventory', label: 'Stok & Inventori' },
  DatabaseBahan: { portal: 'production', page: 'inventory', label: 'Master Bahan Baku' },
  BahanCair: { portal: 'production', page: 'inventory', label: 'Stok Bahan Cair' },
  SampleRacikan: { portal: 'production', page: 'sample', label: 'Sample' },
  RacikanDatabase: { portal: 'production', page: 'sample', label: 'Database Racikan' },
  RiwayatProduksi: { portal: 'production', page: 'po_list', label: 'Daftar PO Produksi' },
  BarangKeluar: { portal: 'production', page: 'delivery', label: 'Delivery' },
  LegalitasBPOM: { portal: 'production', page: 'brands', label: 'Database Brand' },
  ProductionTask: { portal: 'production', page: 'tasks', label: 'Tugas Saya' },
  DailyProduction: { portal: 'production', page: 'reports', label: 'Laporan' },
  QCTest: { portal: 'production', page: 'reject_tracking', label: 'Laporan Reject' },
  
  // Finance
  ClientPO: { portal: 'finance', page: 'invoice_po', label: 'Invoice & PO' },
  AccountMutation: { portal: 'finance', page: 'rekening_kas', label: 'Rekening & Mutasi' },
  OperationalExpense: { portal: 'finance', page: 'operasional', label: 'Biaya Operasional' },
  PayrollRecord: { portal: 'finance', page: 'payroll', label: 'Gaji & Karyawan' },
  ChartOfAccount: { portal: 'finance', page: 'chart_of_accounts', label: 'Daftar Akun (COA)' },
  
  // Purchasing
  PurchasingRequest: { portal: 'purchasing', page: 'purchasing_requests', label: 'Pengajuan Pembelian' },
  PurchasingDocument: { portal: 'purchasing', page: 'documents', label: 'Library Dokumen' },
  VendorItem: { portal: 'purchasing', page: 'items_to_buy', label: 'Item Dibutuhkan PO' },
  
  // Marketing
  ClientAccount: { portal: 'marketing', page: 'client_data', label: 'Data Klien' },
  SPH: { portal: 'marketing', page: 'sph_list', label: 'Daftar SPH' },
  RepeatOrder: { portal: 'marketing', page: 'invoices', label: 'Invoice & PO' },
  
  // Workspace
  TeamMember: { portal: 'workspace', page: 'team', label: 'Pengguna & Role' },
  CompanySettings: { portal: 'workspace', page: 'settings', label: 'Pengaturan Perusahaan' },
  AbsensiRecord: { portal: 'workspace', page: 'absensi', label: 'Absensi (ADMS)' },
  AttendanceLog: { portal: 'workspace', page: 'absensi', label: 'Absensi (ADMS)' },
  PageAccessRule: { portal: 'workspace', page: 'access', label: 'Hak Akses & Notifikasi' }
};

const recordBackgroundAuditLog = (entityName, actionType, id, oldData = null, newData = null) => {
  if (['ActivityLog', 'AsaSystemKnowledge', 'VisitorLog'].includes(entityName)) return;

  setTimeout(async () => {
    try {
      const meta = ENTITY_METADATA[entityName] || { portal: 'workspace', page: 'dashboard_workspace', label: entityName };
      
      // Dynamically detect current portal & sub-page from active URL path
      let detectedPortal = meta.portal;
      let detectedPage = meta.page;

      if (typeof window !== 'undefined' && window.location) {
        const path = window.location.pathname.toLowerCase();
        if (path.includes('/office/production')) detectedPortal = 'production';
        else if (path.includes('/office/finance')) detectedPortal = 'finance';
        else if (path.includes('/office/marketing')) detectedPortal = 'marketing';
        else if (path.includes('/office/purchasing')) detectedPortal = 'purchasing';
        else if (path.includes('/office/workspace')) detectedPortal = 'workspace';
        else if (path.includes('/office/analytics')) detectedPortal = 'analytics';

        // Extract sub-page
        const segments = path.split('/').filter(Boolean);
        if (segments.length >= 3) {
          const lastSeg = segments[segments.length - 1];
          if (lastSeg && lastSeg !== detectedPortal) {
            detectedPage = lastSeg.replace(/-/g, '_');
          }
        }
      }

      const sessionUser = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('satala_member') || '{}') : {};
      const userName = sessionUser.full_name || sessionUser.name || 'Tim Satala';
      const userRole = sessionUser.position || sessionUser.role || 'Staff';

      let title = '';
      let details = '';
      const nameField = newData?.nama_item || newData?.nama_barang || newData?.customerName || newData?.brand_name || newData?.nama || newData?.name || newData?.full_name || id;

      if (actionType === 'create') {
        title = `Menambahkan ${meta.label}: ${nameField}`;
        details = `ID: ${id}`;
      } else if (actionType === 'update') {
        const changes = [];
        if (oldData && newData) {
          const keys = Object.keys(newData);
          for (const k of keys) {
            if (['updated_date', 'created_date', 'id', 'deleted_at'].includes(k)) continue;
            if (typeof oldData[k] !== 'object' && typeof newData[k] !== 'object' && oldData[k] !== undefined && oldData[k] !== newData[k]) {
              changes.push(`${k}: ${oldData[k] || '-'} → ${newData[k] || '-'}`);
            }
          }
        }
        title = `Mengedit ${meta.label}: ${nameField}`;
        details = changes.length > 0 ? `Perubahan: ${changes.slice(0, 4).join(', ')}` : `Data berhasil diperbarui`;
      } else if (actionType === 'delete') {
        title = `Menghapus ${meta.label}: ${nameField}`;
        details = `ID: ${id}`;
      }

      const logPayload = {
        portal: meta.portal,
        page: meta.page,
        action_type: actionType,
        title,
        details,
        target_id: id,
        target_name: String(nameField || ''),
        user_name: userName,
        user_role: userRole,
        created_date: new Date().toISOString()
      };

      await supabase.from('ActivityLog').insert({
        id: 'log_' + Math.random().toString(36).substr(2, 9),
        created_date: logPayload.created_date,
        updated_date: logPayload.created_date,
        data: logPayload
      });
    } catch (e) {
      // Non-blocking silent catch
    }
  }, 0);
};

const createEntityHandler = (entityName) => {
  const handler = {
    list: async (sortBy) => {
      const data = await fetchAllRowsFromTable(entityName);

      const resultData = (data || [])
        .filter(row => !row.data?.deleted_at)
        .map(row => ({ ...row.data, id: row.id, created_date: row.created_date, updated_date: row.updated_date }));

      if (sortBy && typeof sortBy === 'string') {
         const isDesc = sortBy.startsWith('-');
         const field = isDesc ? sortBy.slice(1) : sortBy;
         resultData.sort((a, b) => {
            if (a[field] < b[field]) return isDesc ? 1 : -1;
            if (a[field] > b[field]) return isDesc ? -1 : 1;
            return 0;
         });
      }
      return resultData;
    },
    
    filter: async (queryObj) => {
      const data = await fetchAllRowsFromTable(entityName, queryObj);

      return (data || []).map(row => ({ ...row.data, id: row.id, created_date: row.created_date, updated_date: row.updated_date }))
        .filter(item => !item.deleted_at)
        .filter(item => {
           return Object.keys(queryObj).every(key => String(item[key]) === String(queryObj[key]));
        });
    },
    
    get: async (id) => {
      const { data, error } = await supabase.from(entityName).select('*').eq('id', id).single();
      if (error || !data) return null;
      return { ...data.data, id: data.id, created_date: data.created_date, updated_date: data.updated_date };
    },
    
    create: async (payload) => {
      const id = payload.id || ('id_' + Math.random().toString(36).substr(2, 9));
      
      const rootProps = ['id', 'created_date', 'updated_date'];
      const dataPayload = { ...payload };
      rootProps.forEach(prop => delete dataPayload[prop]);

      const newItem = {
        id,
        created_date: payload.created_date || new Date().toISOString(),
        updated_date: payload.updated_date || new Date().toISOString(),
        data: dataPayload
      };

      const { data, error } = await supabase.from(entityName).insert(newItem).select().single();
      if (error) {
        console.error(`[Supabase] Gagal menyimpan ke ${entityName}:`, error.message);
        throw error;
      }

      recordBackgroundAuditLog(entityName, 'create', id, null, dataPayload);
      
      return { ...data.data, id: data.id, created_date: data.created_date, updated_date: data.updated_date };
    },
    
    update: async (id, payload) => {
      const { data: existingData, error: findError } = await supabase.from(entityName).select('data').eq('id', id).single();
      if (findError || !existingData) {
        console.error(`[Supabase] Data ${entityName} dengan ID ${id} tidak ditemukan.`);
        throw new Error(`Data ${entityName} dengan ID ${id} tidak ditemukan di database.`);
      }
      
      const rootProps = ['id', 'created_date', 'updated_date'];
      const dataPayload = { ...payload };
      rootProps.forEach(prop => delete dataPayload[prop]);

      const mergedData = { ...existingData.data, ...dataPayload };

      const updateData = {
         updated_date: new Date().toISOString(),
         data: mergedData
      };
      
      if (payload.id && payload.id !== id) {
          updateData.id = payload.id;
      }
      
      const { data, error } = await supabase.from(entityName).update(updateData).eq('id', id).select().single();
      if (error) {
        console.error(`[Supabase] Gagal update ${entityName}:`, error.message);
        throw error;
      }

      recordBackgroundAuditLog(entityName, 'update', id, existingData.data, mergedData);

      return { ...data.data, id: data.id, created_date: data.created_date, updated_date: data.updated_date };
    },
    
    delete: async (id, deleted_by) => {
      try {
        const payload = { deleted_at: new Date().toISOString() };
        if (deleted_by) payload.deleted_by = deleted_by;
        await handler.update(id, payload);
        recordBackgroundAuditLog(entityName, 'delete', id);
        return true;
      } catch (error) {
        console.error(`[Supabase] Soft delete gagal untuk ${entityName}:`, error);
        return false;
      }
    },
    
    hardDelete: async (id) => {
      const { error } = await supabase.from(entityName).delete().eq('id', id);
      if (error) {
        console.error(`[Supabase] Hard delete gagal untuk ${entityName}:`, error.message);
        throw error;
      }
      recordBackgroundAuditLog(entityName, 'delete', id);
      return true;
    },

    restore: async (id) => {
      try {
        await handler.update(id, { deleted_at: null });
        return true;
      } catch (error) {
        console.error(`[Supabase] Restore gagal untuk ${entityName}:`, error);
        return false;
      }
    },

    listTrash: async () => {
      const { data, error } = await supabase.from(entityName).select('*');
      if (error) {
         console.error(`[Supabase] Gagal membaca trash ${entityName}:`, error.message);
         return [];
      }
      const resultData = data
        .filter(row => row.data?.deleted_at)
        .map(row => ({ ...row.data, id: row.id, created_date: row.created_date, updated_date: row.updated_date }));
      return resultData.sort((a, b) => new Date(b.deleted_at) - new Date(a.deleted_at));
    }
  };
  return handler;
};

// LocalStorage Mock for PurchasingDocument
const localMockHandler = (entityName) => {
  const getStorage = () => JSON.parse(localStorage.getItem(`mock_${entityName}`) || '[]');
  const setStorage = (data) => localStorage.setItem(`mock_${entityName}`, JSON.stringify(data));

  return {
    list: async (sortBy) => {
      let data = getStorage().filter(item => !item.deleted_at);
      if (sortBy && typeof sortBy === 'string') {
         const isDesc = sortBy.startsWith('-');
         const field = isDesc ? sortBy.slice(1) : sortBy;
         data.sort((a, b) => {
            if (a[field] < b[field]) return isDesc ? 1 : -1;
            if (a[field] > b[field]) return isDesc ? -1 : 1;
            return 0;
         });
      }
      return data;
    },
    filter: async (queryObj) => {
      return getStorage()
        .filter(item => !item.deleted_at)
        .filter(item => Object.keys(queryObj).every(key => String(item[key]) === String(queryObj[key])));
    },
    get: async (id) => getStorage().find(item => item.id === id) || null,
    create: async (payload) => {
      const id = payload.id || ('id_' + Math.random().toString(36).substr(2, 9));
      const newItem = {
        ...payload,
        id,
        created_date: payload.created_date || new Date().toISOString(),
        updated_date: payload.updated_date || new Date().toISOString()
      };
      const data = getStorage();
      data.push(newItem);
      setStorage(data);
      return newItem;
    },
    update: async (id, payload) => {
      const data = getStorage();
      const idx = data.findIndex(item => item.id === id);
      if (idx === -1) throw new Error("Not found");
      data[idx] = { ...data[idx], ...payload, updated_date: new Date().toISOString() };
      setStorage(data);
      return data[idx];
    },
    delete: async (id, deleted_by) => {
      const data = getStorage();
      const idx = data.findIndex(item => item.id === id);
      if (idx !== -1) {
        data[idx].deleted_at = new Date().toISOString();
        if (deleted_by) data[idx].deleted_by = deleted_by;
        setStorage(data);
      }
      return true;
    },
    hardDelete: async (id) => {
      setStorage(getStorage().filter(item => item.id !== id));
      return true;
    },
    restore: async (id) => {
      const data = getStorage();
      const idx = data.findIndex(item => item.id === id);
      if (idx !== -1) {
        delete data[idx].deleted_at;
        setStorage(data);
      }
      return true;
    },
    listTrash: async () => getStorage().filter(item => item.deleted_at).sort((a, b) => new Date(b.deleted_at) - new Date(a.deleted_at))
  };
};

// Sandbox LocalStorage Handler for Developer Testing Mode
const sandboxEntityHandler = (entityName) => {
  const storageKey = `sandbox_db_${entityName}`;
  const getStorage = () => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        // Auto-seed minimal required dev user if TeamMember requested in sandbox
        if (entityName === 'TeamMember') {
          const defaultTeam = [
            {
              id: 'dev_sandbox_member',
              employee_id: 'dev01',
              full_name: 'Developer Satala (Sandbox)',
              role: 'developer',
              position: 'DEVELOPER WEB',
              email: 'dev@satala.com',
              status: 'aktif',
              akses_khusus: { semua_akses: true },
              created_date: new Date().toISOString()
            }
          ];
          localStorage.setItem(storageKey, JSON.stringify(defaultTeam));
          return defaultTeam;
        }
        return [];
      }
      return JSON.parse(raw);
    } catch {
      return [];
    }
  };
  const setStorage = (data) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(data));
    } catch (e) {
      console.error("[Sandbox Storage Error]", e);
    }
  };

  return {
    list: async (sortBy) => {
      let data = getStorage().filter(item => !item.deleted_at);
      if (sortBy && typeof sortBy === 'string') {
         const isDesc = sortBy.startsWith('-');
         const field = isDesc ? sortBy.slice(1) : sortBy;
         data.sort((a, b) => {
            if (a[field] < b[field]) return isDesc ? 1 : -1;
            if (a[field] > b[field]) return isDesc ? -1 : 1;
            return 0;
         });
      }
      return data;
    },
    filter: async (queryObj) => {
      return getStorage()
        .filter(item => !item.deleted_at)
        .filter(item => Object.keys(queryObj).every(key => String(item[key]) === String(queryObj[key])));
    },
    get: async (id) => getStorage().find(item => item.id === id) || null,
    create: async (payload) => {
      const id = payload.id || ('id_' + Math.random().toString(36).substr(2, 9));
      const newItem = {
        ...payload,
        id,
        created_date: payload.created_date || new Date().toISOString(),
        updated_date: payload.updated_date || new Date().toISOString()
      };
      const data = getStorage();
      data.push(newItem);
      setStorage(data);
      return newItem;
    },
    update: async (id, payload) => {
      const data = getStorage();
      const idx = data.findIndex(item => item.id === id);
      if (idx === -1) {
        // Auto create if not found in sandbox
        const created = {
          ...payload,
          id,
          created_date: new Date().toISOString(),
          updated_date: new Date().toISOString()
        };
        data.push(created);
        setStorage(data);
        return created;
      }
      data[idx] = { ...data[idx], ...payload, updated_date: new Date().toISOString() };
      setStorage(data);
      return data[idx];
    },
    delete: async (id, deleted_by) => {
      const data = getStorage();
      const idx = data.findIndex(item => item.id === id);
      if (idx !== -1) {
        data[idx].deleted_at = new Date().toISOString();
        if (deleted_by) data[idx].deleted_by = deleted_by;
        setStorage(data);
      }
      return true;
    },
    hardDelete: async (id) => {
      setStorage(getStorage().filter(item => item.id !== id));
      return true;
    },
    restore: async (id) => {
      const data = getStorage();
      const idx = data.findIndex(item => item.id === id);
      if (idx !== -1) {
        delete data[idx].deleted_at;
        setStorage(data);
      }
      return true;
    },
    listTrash: async () => getStorage().filter(item => item.deleted_at).sort((a, b) => new Date(b.deleted_at) - new Date(a.deleted_at))
  };
};

export const isTestingModeActive = () => {
  if (typeof window === 'undefined') return false;
  const active = localStorage.getItem('satala_dev_sandbox_mode') === 'true';
  if (active) {
    try {
      ensureSandboxInitialized();
    } catch (e) {
      // safe fallback
    }
  }
  return active;
};

export const base44 = {
  entities: new Proxy({}, {
    get: (target, prop) => {
      if (typeof prop === 'symbol' || prop === 'then') return target[prop];

      if (isTestingModeActive()) {
        return sandboxEntityHandler(prop);
      }

      if (!target[prop]) {
        if (prop === 'ChartOfAccount' || prop === 'FinanceTransaction') {
          target[prop] = localMockHandler(prop);
        } else {
          target[prop] = createEntityHandler(prop);
        }
      }
      return target[prop];
    }
  }),
  functions: {
    invoke: async (name, payload) => {
      console.log(`[Supabase Mock] invoked function ${name}`, payload);
      return { success: true, message: 'Mocked function call via Supabase adapter' };
    }
  },
  integrations: {
    Core: {
      UploadFile: async ({ file }) => {
        if (!file) throw new Error('No file provided');
        
        let fileToUpload = file;
        
        // Auto-Compress images to 100-300KB
        if (file.type.startsWith('image/')) {
            try {
                const options = {
                    maxSizeMB: 0.25, // Target max 250 KB
                    maxWidthOrHeight: 800, // Good enough for previewing bottles/labels
                    useWebWorker: true,
                    initialQuality: 0.7,
                };
                fileToUpload = await imageCompression(file, options);
                console.log(`[Auto-Compress] Reduced from ${(file.size/1024).toFixed(1)}KB to ${(fileToUpload.size/1024).toFixed(1)}KB`);
            } catch (error) {
                console.warn("[Auto-Compress] Compression failed, proceeding with original:", error);
            }
        }
        
        if (isTestingModeActive()) {
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = (e) => resolve({ file_url: e.target.result });
                reader.readAsDataURL(fileToUpload);
            });
        }

        // Use Supabase Storage bucket named 'attachments'
        const fileExt = fileToUpload.name ? fileToUpload.name.split('.').pop() : 'png';
        const fileName = `${Math.random().toString(36).substring(2)}_${Date.now()}.${fileExt}`;
        const filePath = `uploads/${fileName}`;

        // Attempt to upload to Supabase
        const { data, error } = await supabase.storage.from('attachments').upload(filePath, fileToUpload);
        
        if (error) {
            console.error("Storage upload failed, falling back to Base64:", error.message);
            // Fallback to Data URL if bucket doesn't exist (prevents total failure)
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = (e) => resolve({ file_url: e.target.result });
                reader.readAsDataURL(file);
            });
        }

        // Get public URL
        const { data: publicUrlData } = supabase.storage.from('attachments').getPublicUrl(filePath);
        return { file_url: publicUrlData.publicUrl };
      }
    }
  }
};

// Seed initial login data if tables are completely empty
const seedInitialData = async () => {
  if (isTestingModeActive()) return;
  try {
     const { data: memberData, error: memberErr } = await supabase.from('TeamMember').select('id');
     if (!memberErr && memberData && memberData.length === 0) {
        console.log("Supabase: Seeding initial TeamMembers...");
        await base44.entities.TeamMember.create({
          employee_id: 'aa',
          email: 'admin@satala.com',
          password: 'aa',
          full_name: 'Admin Satala',
          role: 'admin',
          status: 'aktif',
        });
        await base44.entities.TeamMember.create({
          employee_id: 'bb',
          email: 'dev@satala.com',
          password: 'bb',
          full_name: 'Developer Satala',
          role: 'developer',
          status: 'aktif',
        });
     }

      const { data: clientData, error: clientErr } = await supabase.from('ClientAccount').select('*');
      const devRow = clientData?.find(row => row.data?.email === 'satalawebdeveloper@gmail.com');
      
      if (!clientErr) {
        if (!devRow) {
          console.log("Supabase: Seeding initial ClientAccount...");
          await base44.entities.ClientAccount.create({
            brand_name: 'Satala Dev Client',
            company_name: 'Satala Dev Client Corp',
            email: 'satalawebdeveloper@gmail.com',
            pic: 'Dev Client PIC',
            phone: '081234567890',
            password: 'Indka0900o',
            status: 'verified',
            database_code: '',
            credit_limit: 0,
            created_date: new Date().toISOString()
          });
        } else if (devRow.data?.password !== 'Indka0900o') {
          console.log("Supabase: Updating dev client password...");
          await base44.entities.ClientAccount.update(devRow.id, {
            ...devRow.data,
            password: 'Indka0900o'
          });
        }
      }
  } catch (e) {
     console.error("Failed to seed initial data", e);
  }
};

const seedInitialCOA = async () => {
  if (isTestingModeActive()) return;
  try {
    const coaList = await base44.entities.ChartOfAccount.list();
    if (coaList.length === 0) {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('mock_ChartOfAccount');
      }
      for (const account of CHART_OF_ACCOUNTS) {
        await base44.entities.ChartOfAccount.create({ id: account.id, code: account.id, ...account });
      }
      console.log("Supabase Mock: Seeded initial Chart of Accounts with updated list.");
    }
  } catch (e) {
    console.error("Failed to seed COA", e);
  }
};

seedInitialData();
seedInitialCOA();